import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Menu, X, Send, CheckCircle } from "lucide-react";

const NAV_LINKS = [
  { label: "Leistungen", href: "/#leistungen" },
  { label: "Über Uns", href: "/#ueber-uns" },
  { label: "Vorteile", href: "/#vorteile" },
  { label: "Kontakt", href: "/kontakt" },
];

export default function ContactPage() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Kontakt – Kostenlose Beratung & Terminvereinbarung";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Jetzt kostenlos beraten lassen! Tel: 02641/205474, Bad Neuenahr-Ahrweiler. Mo–Fr 07:00–16:00. Wir freuen uns auf Ihre Anfrage!');
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      const res = await fetch("/kontakt.php", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData).toString(),
      });
      if (res.ok) {
        setSent(true);
        setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
      } else {
        setError("Beim Senden ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut oder rufen Sie uns an.");
      }
    } catch {
      setError("Verbindungsfehler. Bitte versuchen Sie es erneut oder rufen Sie uns direkt an: 02641 / 205474");
    }
    setSending(false);
  };

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      {/* Navigation */}
      <nav className={`nav-bar fixed top-0 left-0 right-0 z-50 bg-white ${scrolled ? "scrolled" : ""}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3 no-underline">
            <img src="/logo-reinert.webp" alt="Malerbetrieb Reinert" style={{ height: 72, width: 'auto' }} />
          </Link>
          <div className="hidden lg:flex items-center gap-10">
            {NAV_LINKS.map((link) => (
              <Link key={link.href} to={link.href} className="nav-link bg-transparent border-none font-body no-underline">{link.label}</Link>
            ))}
            <a href="tel:+492641205474" className="btn-primary no-underline">
              <Phone className="w-4 h-4" />
              02641 / 205474
            </a>
          </div>
          <button className="lg:hidden bg-transparent border-none cursor-pointer p-2 text-reinert-grey-dark" onClick={() => setMenuOpen(true)}>
            <Menu className="w-7 h-7" />
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${menuOpen ? "open" : ""}`}>
        <div className="flex justify-end p-6">
          <button onClick={() => setMenuOpen(false)} className="bg-transparent border-none cursor-pointer text-white"><X className="w-8 h-8" /></button>
        </div>
        <div className="flex flex-col items-start px-10 gap-6 mt-8">
          {NAV_LINKS.map((link) => (
            <Link key={link.href} to={link.href} onClick={() => setMenuOpen(false)} className="mobile-menu-link bg-transparent border-none no-underline">{link.label}</Link>
          ))}
        </div>
      </div>

      {/* Hero */}
      <div className="relative overflow-hidden pt-32 pb-16 md:pt-36 md:pb-20 px-6" style={{ background: '#0033A0' }}>
        <div className="max-w-4xl mx-auto relative z-10">
          <h1 className="font-display font-extrabold text-5xl md:text-6xl text-white mb-4">Kontakt</h1>
          <p className="font-body text-white/80 text-xl">Wir freuen uns auf Ihre Anfrage</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
          
          {/* Form */}
          <div className="lg:col-span-3">
            <h2 className="font-display font-bold text-2xl text-reinert-grey-dark mb-6">Schreiben Sie uns</h2>
            
            {sent ? (
              <div className="p-8 rounded-2xl text-center" style={{ background: '#E8EFF9' }}>
                <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#0033A0' }} />
                <h3 className="font-display font-bold text-xl text-reinert-grey-dark mb-2">Nachricht gesendet!</h3>
                <p className="font-body text-reinert-grey">Vielen Dank für Ihre Anfrage. Wir melden uns schnellstmöglich bei Ihnen.</p>
                <button onClick={() => setSent(false)} className="btn-primary mt-6">Weitere Nachricht senden</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block font-body font-medium text-sm text-reinert-grey-dark mb-2">Name *</label>
                    <input
                      type="text" name="name" required value={formData.name} onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 font-body text-sm focus:outline-none focus:border-reinert-blue-DEFAULT focus:ring-2 focus:ring-reinert-blue-DEFAULT/20"
                      placeholder="Ihr vollständiger Name"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="block font-body font-medium text-sm text-reinert-grey-dark mb-2">E-Mail *</label>
                    <input
                      type="email" name="email" required value={formData.email} onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 font-body text-sm focus:outline-none focus:border-reinert-blue-DEFAULT focus:ring-2 focus:ring-reinert-blue-DEFAULT/20"
                      placeholder="ihre@email.de"
                      data-testid="contact-email"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block font-body font-medium text-sm text-reinert-grey-dark mb-2">Telefon <span className="text-reinert-grey-light">(optional)</span></label>
                    <input
                      type="tel" name="phone" value={formData.phone} onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 font-body text-sm focus:outline-none focus:border-reinert-blue-DEFAULT focus:ring-2 focus:ring-reinert-blue-DEFAULT/20"
                      placeholder="Ihre Telefonnummer"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <label className="block font-body font-medium text-sm text-reinert-grey-dark mb-2">Betreff *</label>
                    <select
                      name="subject" required value={formData.subject} onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 font-body text-sm focus:outline-none focus:border-reinert-blue-DEFAULT focus:ring-2 focus:ring-reinert-blue-DEFAULT/20 bg-white"
                      data-testid="contact-subject"
                    >
                      <option value="">Bitte wählen...</option>
                      <option value="Malerarbeiten">Malerarbeiten</option>
                      <option value="Bodenbeläge">Bodenbeläge</option>
                      <option value="Komplettsanierung">Komplettsanierung</option>
                      <option value="Beratung">Allgemeine Beratung</option>
                      <option value="Sonstiges">Sonstiges</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block font-body font-medium text-sm text-reinert-grey-dark mb-2">Nachricht *</label>
                  <textarea
                    name="message" required rows={6} value={formData.message} onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 font-body text-sm focus:outline-none focus:border-reinert-blue-DEFAULT focus:ring-2 focus:ring-reinert-blue-DEFAULT/20 resize-vertical"
                    placeholder="Beschreiben Sie Ihr Anliegen..."
                    data-testid="contact-message"
                  />
                </div>
                <p className="font-body text-xs text-reinert-grey-light">
                  Mit dem Absenden stimmen Sie unserer <a href="/datenschutz" className="underline">Datenschutzerklärung</a> zu.
                </p>
                {error && <p className="font-body text-sm text-red-600">{error}</p>}
                <button type="submit" disabled={sending} className="btn-primary" data-testid="contact-submit">
                  {sending ? "Wird gesendet..." : <><Send className="w-4 h-4" /> Nachricht senden</>}
                </button>
              </form>
            )}
          </div>

          {/* Contact Info Sidebar */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl p-8" style={{ background: '#F4F7FC' }}>
              <h3 className="font-display font-bold text-lg text-reinert-grey-dark mb-6">Kontaktdaten</h3>
              <div className="space-y-5">
                <a href="tel:+492641205474" className="flex items-start gap-3 no-underline group">
                  <Phone className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#0033A0' }} />
                  <div>
                    <div className="font-body font-semibold text-sm text-reinert-grey-dark group-hover:text-reinert-blue-DEFAULT transition-colors">02641 / 205474</div>
                    <div className="font-body text-xs text-reinert-grey-light">Rufen Sie uns an</div>
                  </div>
                </a>
                <a href="mailto:info@malerbetrieb-reinert.de" className="flex items-start gap-3 no-underline group">
                  <Mail className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#0033A0' }} />
                  <div>
                    <div className="font-body font-semibold text-sm text-reinert-grey-dark group-hover:text-reinert-blue-DEFAULT transition-colors">info@malerbetrieb-reinert.de</div>
                    <div className="font-body text-xs text-reinert-grey-light">Schreiben Sie uns</div>
                  </div>
                </a>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#0033A0' }} />
                  <div>
                    <div className="font-body font-semibold text-sm text-reinert-grey-dark">Walporzheimer Str. 100</div>
                    <div className="font-body text-xs text-reinert-grey-light">53474 Bad Neuenahr-Ahrweiler</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#0033A0' }} />
                  <div>
                    <div className="font-body font-semibold text-sm text-reinert-grey-dark">Öffnungszeiten</div>
                    <div className="font-body text-xs text-reinert-grey-light space-y-0.5 mt-1">
                      <div>Mo – Fr: 07:00 – 16:00</div>
                      <div>Sa – So: Geschlossen</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-2xl p-8" style={{ background: '#FFF8E0' }}>
              <h3 className="font-display font-bold text-lg mb-3" style={{ color: '#0033A0' }}>Schnell & unkompliziert</h3>
              <p className="font-body text-sm text-reinert-grey leading-relaxed">
                Rufen Sie uns einfach an oder nutzen Sie das Formular. Wir melden uns innerhalb von 24 Stunden bei Ihnen zurück.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-reinert-grey-dark py-8 px-6 text-center">
        <p className="text-white/30 font-body text-xs">&copy; {new Date().getFullYear()} Malerbetrieb Reinert. Alle Rechte vorbehalten.</p>
      </footer>
    </div>
  );
}
